import { protectedRequest } from '@/api/protectedRequest'
import http from '@/api/http'
import { mapBasketItem } from '@/entities/basket/model/mapBasketItem'
import { toApiPaymentMethod } from '@/entities/payment/model/orderPayment'

export function mapBasketResponse(data) {
  const restaurantId =
    data?.resto_id != null && data.resto_id !== '' ? String(data.resto_id) : null
  const list = Array.isArray(data?.items) ? data.items : []

  return {
    items: list.map((raw) => mapBasketItem(raw)),
    restaurantId,
    hasUnavailable: data?.has_unavailable === true,
  }
}

export function mapBasketQuote(data) {
  if (!data || typeof data !== 'object') {
    return null
  }

  const amountRub = Number(data.amount_rub)
  const discountVnd = Number(data.discount_vnd)
  const promoCode = String(data.promo_code ?? '').trim() || null
  const promoErrorRaw = data.promo_error
  const promoErrorCode =
    promoErrorRaw && typeof promoErrorRaw === 'object'
      ? String(promoErrorRaw.error ?? '').trim()
      : ''
  const promoErrorMessage =
    promoErrorRaw && typeof promoErrorRaw === 'object'
      ? String(promoErrorRaw.message ?? '').trim()
      : ''
  const promoErrorDetails =
    promoErrorRaw && typeof promoErrorRaw === 'object' ? (promoErrorRaw.details ?? null) : null

  const zoneRaw = data.zone
  const zoneName =
    zoneRaw && typeof zoneRaw === 'object' ? String(zoneRaw.name ?? '').trim() : ''
  const zoneCode =
    zoneRaw && typeof zoneRaw === 'object' ? String(zoneRaw.code ?? '').trim() : ''

  return {
    itemsCount: Number(data.items_count) || 0,
    itemsTotalVnd: Number(data.items_total_vnd) || 0,
    deliveryFeeVnd: Number(data.delivery_fee_vnd) || 0,
    amountDueVnd: Number(data.amount_due_vnd) || 0,
    amountRub: Number.isFinite(amountRub) ? amountRub : null,
    cashOnDeliveryAllowed: data.cash_on_delivery_allowed !== false,
    zone: zoneName || zoneCode ? { code: zoneCode || null, name: zoneName || null } : null,
    promoCode,
    discountVnd: Number.isFinite(discountVnd) ? Math.max(0, discountVnd) : 0,
    promoError:
      promoErrorCode || promoErrorMessage
        ? {
            code: promoErrorCode || null,
            message: promoErrorMessage || null,
            details: promoErrorDetails,
          }
        : null,
  }
}

export async function fetchBasketApi() {
  const payload = await protectedRequest(() => http.get('/personal/basket'))
  return mapBasketResponse(payload.data)
}

export async function fetchBasketQuoteApi(paymentMethod, location = null) {
  const params = {
    payment_method: toApiPaymentMethod(paymentMethod),
  }

  const lat = Number(location?.lat)
  const lng = Number(location?.lng)
  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    params.lat = lat
    params.lng = lng
  }

  const payload = await protectedRequest(() =>
    http.get('/personal/basket/quote', {
      params,
    }),
  )
  return mapBasketQuote(payload.data)
}

export async function applyBasketPromoApi(code, location = null) {
  const normalized =
    code == null || String(code).trim() === '' ? null : String(code).trim()

  const body = { code: normalized }
  if (normalized) {
    const lat = Number(location?.lat)
    const lng = Number(location?.lng)
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      body.lat = lat
      body.lng = lng
    }
  }

  await protectedRequest(() => http.post('/personal/basket/promo', body))
}

export async function addToBasketApi({ productId, quantity, modifiers }) {
  const body = {
    product_id: productId,
    quantity,
  }

  if (modifiers && Object.keys(modifiers).length) {
    body.modifiers = modifiers
  }

  await protectedRequest(() => http.post('/personal/basket/add', body))
}

export async function removeFromBasketApi({ productId, quantity, lineKey }) {
  const body = { product_id: productId }
  if (quantity != null) {
    body.quantity = quantity
  }
  if (lineKey) {
    body.line_key = lineKey
  }
  await protectedRequest(() => http.post('/personal/basket/remove', body))
}
